import type { PageField, PageView } from '~/pages/AskRichardUnified/page/pageContract';
import { validatePageFilters } from '~/pages/AskRichardUnified/page/pageFilters';

import {
  BOARD_SELECTS,
  BOARD_VIEWS,
  type BoardFilters,
  PRODUCT_CATEGORIES,
  SALES_PERIODS,
  YEAR_TYPES,
} from './boardModel';
import { METRICS, METRIC_TABS, METRIC_VIEWS, type MetricId, metricParam } from './metrics';
import { SNAPSHOT_FILTERS, type SnapshotFilters } from './snapshotModel';

const VISIBLE_ROWS = 25;
const SOURCE = 'lux-analytics-prod.analytics.sales_with_refunds, queried live; rebuilt daily around 04:00 Sydney.';

export type PageSection = {
  title: string;
  rows: Array<{ label: string; values: Record<string, number | null> }>;
  truncated: boolean;
};

const visible = (sections: Array<PageSection>) =>
  sections.map((section) => ({
    title: section.title,
    totalRows: section.rows.length,
    rows: section.rows.slice(0, VISIBLE_ROWS).map((row) => ({ label: row.label, ...row.values })),
    truncated: section.truncated || section.rows.length > VISIBLE_ROWS,
  }));

function selectSchema(
  keys: ReadonlyArray<{ key: string }>,
  options: Record<string, Array<string>>,
): Record<string, PageField> {
  return Object.fromEntries(keys.map(({ key }) => [key, { type: 'enum', values: [null, ...(options[key] ?? [])] }]));
}

const singleOrNull = (values: Array<string>) => (values.length === 1 ? values[0] : null);

export function dailySnapshotPageView(input: {
  ready: boolean;
  filters: SnapshotFilters;
  options: Record<string, Array<string>>;
  summary: Record<string, number | null | string> | null;
  sections: Array<PageSection>;
}): PageView {
  return {
    title: 'Daily Snapshot | Sales & Gross Margin',
    ready: input.ready,
    dataMode: 'live analytics.sales_with_refunds',
    filters: Object.fromEntries(SNAPSHOT_FILTERS.map(({ key }) => [key, singleOrNull(input.filters[key])])),
    filterSchema: selectSchema(SNAPSHOT_FILTERS, input.options),
    summary: input.summary,
    visibleSections: visible(input.sections),
    definitions: {
      source: SOURCE,
      dates: 'Sydney transaction dates. Yesterday, month to date and financial year to date all end yesterday.',
      measures:
        'Gross Sales (BR) sums gross_sales_aud. Gross Margin (AR) sums margin_aud - margin_aud_refund. Gross Margin (AR) % divides it by gross_sales_aud - accounting_refund_amount_aud. Category tables use the adjusted measures.',
      forecasts:
        'Rest of month = average daily value over the 21 days to yesterday x days until end of month (0 on the last day). End of month = MTD + rest of month, or rest of month alone on the 1st.',
    },
  };
}

export function snapshotEditFromBridge(changes: Record<string, unknown>, view: PageView, filters: SnapshotFilters) {
  validatePageFilters(changes, view.filterSchema);
  const next = { ...filters };
  for (const { key } of SNAPSHOT_FILTERS) {
    if (key in changes) {
      const value = changes[key] as string | null;
      next[key] = value === null ? [] : [value];
    }
  }
  return next;
}

export function dailyBoardPageView(input: {
  ready: boolean;
  filters: BoardFilters;
  options: Record<string, Array<string>>;
  period: string;
  summary: Record<string, number | null> | null;
  sections: Array<PageSection>;
}): PageView {
  const { filters } = input;
  return {
    title: 'Daily Board',
    ready: input.ready,
    dataMode: 'live analytics.sales_with_refunds',
    filters: {
      view: filters.view,
      salesPeriod: filters.salesPeriod,
      yearType: filters.yearType,
      productCategory: filters.productCategory,
      includeFlights: filters.includeFlights,
      removeAddonMigration: filters.removeAddonMigration,
      ...Object.fromEntries(BOARD_SELECTS.map(({ key }) => [key, singleOrNull(filters.selections[key])])),
      ...Object.fromEntries(METRIC_VIEWS.map((view) => [metricParam(view), filters.metrics[view]])),
    },
    filterSchema: {
      view: { type: 'enum', values: BOARD_VIEWS.map((view) => view.value) },
      salesPeriod: { type: 'enum', values: [...SALES_PERIODS] },
      yearType: { type: 'enum', values: [...YEAR_TYPES] },
      productCategory: { type: 'enum', values: [...PRODUCT_CATEGORIES] },
      includeFlights: { type: 'boolean' },
      removeAddonMigration: { type: 'boolean' },
      ...selectSchema(BOARD_SELECTS, input.options),
      ...Object.fromEntries(
        METRIC_VIEWS.map((view): [string, PageField] => [
          metricParam(view),
          { type: 'enum', values: METRIC_TABS[view].options.map((option) => option.metric) },
        ]),
      ),
    },
    period: input.period,
    summary: input.summary,
    visibleSections: visible(input.sections),
    definitions: {
      source: SOURCE,
      dates:
        'Sydney transaction dates. The sales period ends yesterday and is compared with the window before it; monthly views show 13 months, yearly views 3 years, refunds 5 quarters.',
      measures:
        'Gross sales sums gross_sales_aud; refunds sum accounting_refund_amount_aud; net sales is their difference; margin is margin_aud - margin_aud_refund. Orders, customers and items are distinct counts on sale rows. AOV = gross sales / orders; attach rate = orders with the product / orders.',
      filters: 'Each dropdown accepts one value or null for all values here.',
      metrics: `Each *Metric field is that tab's Metrics Filter: ${Object.entries(METRICS)
        .map(([id, metric]) => `${id} = ${metric.label}`)
        .join('; ')}.`,
    },
  };
}

export function boardEditFromBridge(
  changes: Record<string, unknown>,
  view: PageView,
  filters: BoardFilters,
): BoardFilters {
  validatePageFilters(changes, view.filterSchema);
  const next: BoardFilters = { ...filters, selections: { ...filters.selections } };
  if ('view' in changes) {
    next.view = changes.view as BoardFilters['view'];
  }
  if ('salesPeriod' in changes) {
    next.salesPeriod = changes.salesPeriod as BoardFilters['salesPeriod'];
  }
  if ('yearType' in changes) {
    next.yearType = changes.yearType as BoardFilters['yearType'];
  }
  if ('productCategory' in changes) {
    next.productCategory = changes.productCategory as string;
  }
  if ('includeFlights' in changes) {
    next.includeFlights = Boolean(changes.includeFlights);
  }
  if ('removeAddonMigration' in changes) {
    next.removeAddonMigration = Boolean(changes.removeAddonMigration);
  }
  for (const { key } of BOARD_SELECTS) {
    if (key in changes) {
      const value = changes[key] as string | null;
      next.selections[key] = value === null ? [] : [value];
    }
  }
  for (const view of METRIC_VIEWS) {
    if (metricParam(view) in changes) {
      next.metrics = { ...next.metrics, [view]: changes[metricParam(view)] as MetricId };
    }
  }
  return next;
}
